import { Box, Button } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinTheme } from "@src/appV2/redesign/theme/theme";
import { type ReactElement, useCallback, useState } from "react";

import { AnimatedBackgroundPageWrapper } from "../layouts/AnimatedBackgroundPageWrapper";
import { MobileLoginWithEmailAndPasswordForm } from "../LoginWithEmailAndPassword/MobileLoginWithEmailAndPasswordForm";
import { loginWithEmailAndPassword } from "../LoginWithEmailAndPassword/loginWithEmailAndPassword";
import { MobileLoginForm } from "./MobileLoginForm";
import { sendLoginEmail } from "./sendLoginEmail";

type LoginMode = "magicLink" | "password";

export function MobileLogin(): ReactElement {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>("magicLink");

  const handleMagicLinkSubmit = useCallback(async () => {
    if (!email) {
      return;
    }

    setLoading(true);

    const success = await sendLoginEmail({
      email,
      requestedURL: localStorage.getItem("redirectURL") ?? undefined,
    });

    if (success) {
      setEmailSent(true);
    }

    setLoading(false);
  }, [email]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!email || !password) {
      return;
    }

    setLoading(true);

    const success = await loginWithEmailAndPassword({ email, password });

    if (success) {
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [email, password]);

  return (
    <AnimatedBackgroundPageWrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: 3,
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            marginBottom: 4,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={`${process.env.REACT_APP_BASE_PATH ?? "/"}brownAndRedBunny.png`}
            alt="Clipboard"
            style={{ height: 150, width: "auto" }}
          />
        </Box>

        {/* Content */}
        <ThemeProvider theme={berlinTheme}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
            }}
          >
            {loginMode === "magicLink" && !emailSent && (
              <MobileLoginForm
                email={email}
                loading={loading}
                setEmail={setEmail}
                onSubmit={handleMagicLinkSubmit}
                onSwitchToPassword={() => {
                  setLoginMode("password");
                }}
              />
            )}

            {loginMode === "magicLink" && emailSent && (
              <Box sx={{ textAlign: "center", padding: 2 }}>
                <Text bold variant="h5" sx={{ color: "success.main", marginBottom: 2 }}>
                  ✓ Verification Link Sent
                </Text>
                <Text variant="body1" sx={{ color: "text.secondary" }}>
                  We have sent an email with a login link to <strong>{email}</strong>.
                </Text>
                <Button
                  variant="text"
                  sx={{ marginTop: 3, textTransform: "none" }}
                  onClick={() => {
                    setEmailSent(false);
                  }}
                >
                  Send another email
                </Button>
              </Box>
            )}

            {loginMode === "password" && (
              <>
                <MobileLoginWithEmailAndPasswordForm
                  email={email}
                  password={password}
                  loading={loading}
                  setEmail={setEmail}
                  setPassword={setPassword}
                  onSubmit={handlePasswordSubmit}
                />
                <Box sx={{ textAlign: "center", marginTop: 3 }}>
                  <Button
                    variant="text"
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                    onClick={() => {
                      setLoginMode("magicLink");
                    }}
                  >
                    Sign in with magic link
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </ThemeProvider>
      </Box>
    </AnimatedBackgroundPageWrapper>
  );
}
